'use client';

import cartIcon from '@/assets/images/icons/icon-cart.png';
import menuIcon from '@/assets/images/icons/icon-menu.png';
import profileIcon from '@/assets/images/icons/icon-profile.png';
import ImageIcon from '@/components/ui/ImageIcon/ImageIcon';
import LoadingLink from '@/components/ui/LoadingLink/LoadingLink';
import { PRODUCT_NAME } from '@/constants';
import { ShueishaIdAuthType } from '@/features/account/types';
import { goShueishaIdAuth } from '@/features/auth/utils';
import AdminModeBanner from '@/features/notification/components/AdminModeBanner/AdminModeBanner';
import SiteNotification from '@/features/notification/components/SiteNotification/SiteNotification';
import type { SiteNotificationItem } from '@/features/notification/types';
import { useCurrentPathname } from '@/hooks/useCurrentPathname';
import { useCartInfoAtom, useGlobalLoadingAtom, useLoginCartCountAtom } from '@/state';
import { getCartCount } from '@/utils/cartCount';
import useIsMovingToOtherPath from '@/utils/useIsMovingToOtherPath';
import type { Route } from 'next';
import { useSession } from 'next-auth/react';
import { useRouter } from 'next/navigation';
import { useEffect, useState } from 'react';
import logo from '/public/logo.png';
import { useIsMenuOpenedAtom } from '../CommonMenu/CommonMenu';
import styles from './CommonHeaderSp.module.scss';

function CommonHeaderSp({
  navButtonDispFlg = true,
  cartCount = 0,
  latestNotification,
  isStaff = false,
}: {
  navButtonDispFlg?: boolean;
  cartCount?: number;
  latestNotification?: SiteNotificationItem[];
  isStaff?: boolean;
}) {
  const [localCartInfo] = useCartInfoAtom();
  const [loginCartCount, setLoginCartCount] = useLoginCartCountAtom();
  const router = useRouter();
  const session = useSession();
  const currentPathname = useCurrentPathname();
  const [_, startGlobalLoading] = useGlobalLoadingAtom();
  const isMovingToOtherPath = useIsMovingToOtherPath();

  // SSRで渡されたcartCountでatomを初期化
  useEffect(() => {
    if (cartCount > 0 && loginCartCount === 0) {
      setLoginCartCount(cartCount);
    }
  }, [cartCount]);

  const handleOnProfileClick = async (e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    // 既にサインインページにいる場合は、何もしない（現在のURLを維持）
    if (currentPathname.startsWith('/auth/signin')) {
      return;
    }
    if (session.data) {
      if (isMovingToOtherPath('/account')) {
        startGlobalLoading();
      }
      router.push('/account');
      return;
    }
    // 未ログイン：サインインページへ
    if (isMovingToOtherPath('/auth/signin')) {
      startGlobalLoading();
    }

    router.push(`/auth/signin?callbackUrl=${encodeURIComponent(currentPathname)}`);
  };

  const cartBadgeCount = getCartCount(!!session.data, loginCartCount, localCartInfo);

  const [isMenuOpenedValue, setIsMenuOpened] = useIsMenuOpenedAtom();
  const handleOnMenuClick = () => {
    setIsMenuOpened(true);
  };

  return (
    <>
      <div className={`${styles.componentStyle} ${isStaff ? styles.componentStyleWithAdminMode : ''}`}>
        {isStaff && <AdminModeBanner />}
        {latestNotification && latestNotification.length > 0 && (
          <SiteNotification link={latestNotification[0].link} title={latestNotification[0].title} />
        )}
        <header className={`${styles.header} ${isMenuOpenedValue ? styles.menuOpened : ''}`}>
          <div className={styles.container}>
            <LoadingLink href={'/'} className={styles.logo}>
              <img src={logo.src} alt={PRODUCT_NAME} />
            </LoadingLink>
            <div className={styles.iconContainer}>
              {navButtonDispFlg && (
                <>
                  <div onClick={handleOnProfileClick}>
                    {session.data ? (
                      <ImageIcon imgSrc={profileIcon.src} />
                    ) : (
                      <p className={styles.loginText}>新規登録 / LOGIN</p>
                    )}
                  </div>
                  <div
                    className={styles.profileIcon}
                    onClick={() => {
                      if (isMovingToOtherPath('/cart')) {
                        startGlobalLoading();
                      }
                      router.push('/cart');
                    }}
                  >
                    <ImageIcon imgSrc={cartIcon.src} badgeCount={cartBadgeCount} />
                  </div>
                </>
              )}
              <div onClick={handleOnMenuClick} data-testid='hamburger-menu-button'>
                <ImageIcon imgSrc={menuIcon.src} />
              </div>
            </div>
          </div>
        </header>
      </div>
    </>
  );
}

export default CommonHeaderSp;
